import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { FRIDGE_URL, KITCHEN_URL, BATHROOM_URL, TUB_BEFORE_URL, TUB_AFTER_URL } from "@/constants/assets";

const CompareSlider = ({ splitImage, beforeUrl, afterUrl, testid }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    ref.current.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && update(e.clientX);
  const onPointerUp = () => { dragging.current = false; };

  const afterStyle = splitImage
    ? { backgroundImage: `url(${splitImage})`, backgroundSize: "200% 100%", backgroundPosition: "right center" }
    : { backgroundImage: `url(${afterUrl})`, backgroundSize: "cover", backgroundPosition: "center" };
  const beforeStyle = splitImage
    ? { width: `${pos}%`, backgroundImage: `url(${splitImage})`, backgroundSize: "200% 100%", backgroundPosition: "left center" }
    : { width: `${pos}%`, backgroundImage: `url(${beforeUrl})`, backgroundSize: "cover", backgroundPosition: "center" };

  return (
    <div
      ref={ref}
      data-testid={testid}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="relative w-full aspect-[4/3] md:aspect-[16/10] rounded-3xl overflow-hidden border border-[#E5DEC9] shadow-lg select-none touch-none cursor-ew-resize bg-white"
    >
      <div className="absolute inset-0" style={afterStyle} />
      <div className="absolute inset-y-0 left-0" style={beforeStyle} />
      <span className="absolute top-4 left-4 bg-[#1A1A1A]/85 text-white text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full">Before</span>
      <span className="absolute top-4 right-4 bg-[#990000] text-white text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full">After</span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-white border-2 border-[#990000] text-[#990000] flex items-center justify-center shadow-xl">
          <MoveHorizontal size={19} />
        </div>
      </div>
    </div>
  );
};

const shots = [
  { src: KITCHEN_URL, label: "Kitchen reset", note: "Degreased, polished & photo-ready", testid: "gallery-kitchen" },
  { src: BATHROOM_URL, label: "Bathroom sparkle", note: "Grout, glass & fixtures detailed", testid: "gallery-bathroom" },
];

const BeforeAfter = () => (
  <section id="before-after" className="py-20 md:py-28 bg-[#F3EDE4] border-y border-[#E5DEC9]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#990000] mb-3">Proof, not promises</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug">
            Drag the handle. <span className="italic text-[#990000]">See the Tidy Tusks difference.</span>
          </h2>
        </div>
        <p className="text-sm text-[#5C5955] max-w-sm leading-relaxed">Real before-and-after results from a Tidy Tusks deep clean — no filters, no staging, just elbow grease.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <CompareSlider splitImage={FRIDGE_URL} testid="before-after-slider" />
            <p className="text-xs text-[#5C5955] mt-3 font-medium uppercase tracking-[0.14em]">Refrigerator deep clean — drag to compare</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.6 }}>
            <CompareSlider beforeUrl={TUB_BEFORE_URL} afterUrl={TUB_AFTER_URL} testid="before-after-slider-tub" />
            <p className="text-xs text-[#5C5955] mt-3 font-medium uppercase tracking-[0.14em]">Bathtub deep clean — drag to compare</p>
          </motion.div>
        </div>
        <div className="grid grid-rows-2 gap-6">
          {shots.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + i * 0.1, duration: 0.6 }}
              className="group relative rounded-3xl overflow-hidden border border-[#E5DEC9] shadow-sm hover:shadow-lg transition-shadow duration-300"
            >
              <img src={s.src} alt={s.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/75 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <div className="font-display text-white text-lg font-semibold">{s.label}</div>
                <div className="text-white/75 text-xs mt-0.5">{s.note}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default BeforeAfter;
